import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {ButtonModule} from "primeng/button";
import {CommonModule} from "@angular/common";
import {UserService} from "../../services/user.service";
import {ActivatedRoute} from "@angular/router";
import {ApartmentService} from "../../services/apartment.service";
import {FeedbackService} from "../../services/feedback.service";

@Component({
  standalone: true,
  selector: 'app-exports-tab',
  imports: [
    CommonModule,
    ButtonModule,
  ],
  templateUrl: './exports-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportsTabComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly apartmentService = inject(ApartmentService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly route = inject(ActivatedRoute);
  protected readonly userLoading = signal(false);
  protected readonly userMessage = signal<string | undefined>(undefined);
  protected readonly userError = signal<string | undefined>(undefined);
  protected readonly lotteryLoading = signal(false);
  protected readonly lotteryMessage = signal<string | undefined>(undefined);
  protected readonly lotteryError = signal<string | undefined>(undefined);
  protected readonly cancelLoading = signal(false);
  protected readonly cancelMessage = signal<string | undefined>(undefined);
  protected readonly cancelError = signal<string | undefined>(undefined);
  protected readonly applicationsLoading = signal(false);
  protected readonly applicationsMessage = signal<string | undefined>(undefined);
  protected readonly applicationsError = signal<string | undefined>(undefined);
  protected readonly unknownEmailsLoading = signal(false);
  protected readonly unknownEmailsMessage = signal<string | undefined>(undefined);
  protected readonly unknownEmailsError = signal<string | undefined>(undefined);
  protected readonly customerDataLoading = signal(false);
  protected readonly customerDataMessage = signal<string | undefined>(undefined);
  protected readonly customerDataError = signal<string | undefined>(undefined);
  protected readonly marketResearchQuestionsLoading = signal(false);
  protected readonly marketResearchQuestionsMessage = signal<string | undefined>(undefined);
  protected readonly marketResearchQuestionsError = signal<string | undefined>(undefined);
  protected readonly ratingsLoading = signal(false);
  protected readonly ratingsMessage = signal<string | undefined>(undefined);
  protected readonly ratingsError = signal<string | undefined>(undefined);

  ngOnInit(): void {
    const csvKey = this.route.snapshot.queryParamMap.get('csvKey');
    if (csvKey) {
      this.startUserDownload(csvKey);
    }

    const lotteryCsvKey = this.route.snapshot.queryParamMap.get('lotteryCsvKey');
    if (lotteryCsvKey) {
      this.startLotteryDownload(lotteryCsvKey);
    }

    const cancelEventsCsvKey = this.route.snapshot.queryParamMap.get('cancelEventsCsvKey');
    if (cancelEventsCsvKey) {
      this.startCancelEventsDownload(cancelEventsCsvKey);
    }

    const applicationsCsvKey = this.route.snapshot.queryParamMap.get('applicationsCsvKey');
    if (applicationsCsvKey) {
      this.startApplicationsDownload(applicationsCsvKey);
    }

    const unknownEmailsCsvKey = this.route.snapshot.queryParamMap.get('unknownEmailsCsvKey');
    if (unknownEmailsCsvKey) {
      this.startUnknownEmailsDownload(unknownEmailsCsvKey);
    }

    const customerDataCsvKey = this.route.snapshot.queryParamMap.get('customerDataCsvKey');
    if (customerDataCsvKey) {
      this.startCustomerDataDownload(customerDataCsvKey);
    }

    const marketResearchQuestionsCsvKey = this.route.snapshot.queryParamMap.get('marketResearchQuestionsCsvKey');
    if (marketResearchQuestionsCsvKey) {
      this.startMarketResearchQuestionsDownload(marketResearchQuestionsCsvKey);
    }

    const ratingsCsvKey = this.route.snapshot.queryParamMap.get('ratingsCsvKey');
    if (ratingsCsvKey) {
      this.startRatingsDownload(ratingsCsvKey);
    }
  }

  protected requestUserExport() {
    this.userMessage.set(undefined);
    this.userError.set(undefined);
    this.userLoading.set(true);
    this.userService.requestCsvExport().subscribe({
      next: () => {
        this.userMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.userError.set('Failed to request export. Please try again.');
      },
      complete: () => this.userLoading.set(false),
    });
  }

  protected requestCancelEventsExport() {
    this.cancelMessage.set(undefined);
    this.cancelError.set(undefined);
    this.cancelLoading.set(true);
    this.userService.requestCancelEventsExport().subscribe({
      next: () => {
        this.cancelMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.cancelError.set('Failed to request export. Please try again.');
      },
      complete: () => this.cancelLoading.set(false),
    });
  }

  protected requestLotteryExport() {
    this.lotteryMessage.set(undefined);
    this.lotteryError.set(undefined);
    this.lotteryLoading.set(true);
    this.apartmentService.requestLotteryEligibilityExport().subscribe({
      next: () => {
        this.lotteryMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.lotteryError.set('Failed to request export. Please try again.');
      },
      complete: () => this.lotteryLoading.set(false),
    });
  }

  private startUserDownload(s3Key: string) {
    this.userMessage.set('Preparing download link...');
    this.userLoading.set(true);
    this.userService.getCsvDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'users.csv';
        anchor.click();
        this.userMessage.set('Download started.');
      },
      error: () => {
        this.userError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.userLoading.set(false),
    });
  }

  private startCancelEventsDownload(s3Key: string) {
    this.cancelMessage.set('Preparing download link...');
    this.cancelLoading.set(true);
    this.userService.getCancelEventsDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'cancel-events.csv';
        anchor.click();
        this.cancelMessage.set('Download started.');
      },
      error: () => {
        this.cancelError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.cancelLoading.set(false),
    });
  }

  protected requestApplicationsExport() {
    this.applicationsMessage.set(undefined);
    this.applicationsError.set(undefined);
    this.applicationsLoading.set(true);
    this.apartmentService.requestApplicationsExport().subscribe({
      next: () => {
        this.applicationsMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.applicationsError.set('Failed to request export. Please try again.');
      },
      complete: () => this.applicationsLoading.set(false),
    });
  }

  private startApplicationsDownload(s3Key: string) {
    this.applicationsMessage.set('Preparing download link...');
    this.applicationsLoading.set(true);
    this.apartmentService.getApplicationsDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'applications.csv';
        anchor.click();
        this.applicationsMessage.set('Download started.');
      },
      error: () => {
        this.applicationsError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.applicationsLoading.set(false),
    });
  }

  protected requestUnknownEmailsExport() {
    this.unknownEmailsMessage.set(undefined);
    this.unknownEmailsError.set(undefined);
    this.unknownEmailsLoading.set(true);
    this.userService.requestUnknownEmailsExport().subscribe({
      next: () => {
        this.unknownEmailsMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.unknownEmailsError.set('Failed to request export. Please try again.');
      },
      complete: () => this.unknownEmailsLoading.set(false),
    });
  }

  protected requestCustomerDataExport() {
    this.customerDataMessage.set(undefined);
    this.customerDataError.set(undefined);
    this.customerDataLoading.set(true);
    this.userService.requestCustomerDataExport().subscribe({
      next: () => {
        this.customerDataMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.customerDataError.set('Failed to request export. Please try again.');
      },
      complete: () => this.customerDataLoading.set(false),
    });
  }

  private startCustomerDataDownload(s3Key: string) {
    this.customerDataMessage.set('Preparing download link...');
    this.customerDataLoading.set(true);
    this.userService.getCustomerDataDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'customer-data.csv';
        anchor.click();
        this.customerDataMessage.set('Download started.');
      },
      error: () => {
        this.customerDataError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.customerDataLoading.set(false),
    });
  }

  private startUnknownEmailsDownload(s3Key: string) {
    this.unknownEmailsMessage.set('Preparing download link...');
    this.unknownEmailsLoading.set(true);
    this.userService.getUnknownEmailsDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'unknown-emails.csv';
        anchor.click();
        this.unknownEmailsMessage.set('Download started.');
      },
      error: () => {
        this.unknownEmailsError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.unknownEmailsLoading.set(false),
    });
  }

  protected requestMarketResearchQuestionsExport() {
    this.marketResearchQuestionsMessage.set(undefined);
    this.marketResearchQuestionsError.set(undefined);
    this.marketResearchQuestionsLoading.set(true);
    this.feedbackService.requestMarketResearchQuestionsExport().subscribe({
      next: () => {
        this.marketResearchQuestionsMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.marketResearchQuestionsError.set('Failed to request export. Please try again.');
      },
      complete: () => this.marketResearchQuestionsLoading.set(false),
    });
  }

  protected requestRatingsExport() {
    this.ratingsMessage.set(undefined);
    this.ratingsError.set(undefined);
    this.ratingsLoading.set(true);
    this.feedbackService.requestRatingsExport().subscribe({
      next: () => {
        this.ratingsMessage.set('Export requested. We will email the download link once it is ready.');
      },
      error: () => {
        this.ratingsError.set('Failed to request export. Please try again.');
      },
      complete: () => this.ratingsLoading.set(false),
    });
  }

  private startRatingsDownload(s3Key: string) {
    this.ratingsMessage.set('Preparing download link...');
    this.ratingsLoading.set(true);
    this.feedbackService.getRatingsDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'ratings.csv';
        anchor.click();
        this.ratingsMessage.set('Download started.');
      },
      error: () => {
        this.ratingsError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.ratingsLoading.set(false),
    });
  }

  private startMarketResearchQuestionsDownload(s3Key: string) {
    this.marketResearchQuestionsMessage.set('Preparing download link...');
    this.marketResearchQuestionsLoading.set(true);
    this.feedbackService.getMarketResearchQuestionsDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'market-research-questions.csv';
        anchor.click();
        this.marketResearchQuestionsMessage.set('Download started.');
      },
      error: () => {
        this.marketResearchQuestionsError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.marketResearchQuestionsLoading.set(false),
    });
  }

  private startLotteryDownload(s3Key: string) {
    this.lotteryMessage.set('Preparing download link...');
    this.lotteryLoading.set(true);
    this.apartmentService.getLotteryEligibilityDownloadUrl(s3Key).subscribe({
      next: ({url}) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'lottery-eligibility.csv';
        anchor.click();
        this.lotteryMessage.set('Download started.');
      },
      error: () => {
        this.lotteryError.set('Failed to fetch download link. Please try again.');
      },
      complete: () => this.lotteryLoading.set(false),
    });
  }
}
